import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Grid, Chip, Button, Divider, IconButton, Stack,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  CircularProgress, Alert,
} from '@mui/material';
import {
  ArrowBack, Edit, PictureAsPdf, Download, Email, Receipt,
  Business, AccountBalance, AttachFile, UploadFile, Delete, OpenInNew,
  InsertDriveFile, Image as ImageIcon, PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import StatusChip from '../common/StatusChip';

const fmtMoney = (v, ccy) => v == null ? '—'
  : `${ccy || ''} ${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim();
const fmtPrice = (v, ccy) => v == null ? '—'
  : `${ccy || ''} ${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`.trim();
const fmtQty   = (v) => v == null ? '—' : Number(v).toLocaleString('en-US', { maximumFractionDigits: 3 });
const fmtDate  = (d) => !d ? '—' : (typeof d === 'string' ? d.substring(0, 10) : String(d).substring(0, 10));

const Field = ({ label, value, mono }) => (
  <Box sx={{ mb: 1.25 }}>
    <Typography variant="caption" color="text.secondary"
      sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.5 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ mt: 0.3, fontFamily: mono ? 'monospace' : undefined, whiteSpace: 'pre-wrap' }}>
      {value || '—'}
    </Typography>
  </Box>
);

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv,    setInv]    = useState(null);
  const [items,  setItems]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,  setError]  = useState('');
  const [files,   setFiles]   = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/invoices/${id}`);
      const d = r.data.data || {};
      setInv(d);
      setItems(d.items || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load invoice');
    }
    setLoading(false);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // ── Miscellaneous attachments ──────────────────────────────────────────
  const loadFiles = useCallback(async () => {
    try { const r = await api.get(`/invoices/${id}/files`); setFiles(r.data.data || []); }
    catch { /* a missing attachment list must never break the invoice page */ }
  }, [id]);
  useEffect(() => { loadFiles(); }, [loadFiles]);

  const uploadFiles = async (list) => {
    const chosen = Array.from(list || []);
    if (!chosen.length) return;
    setUploading(true); setError('');
    try {
      const fd = new FormData();
      chosen.slice(0, 10).forEach(f => fd.append('files', f, f.name));
      await api.post(`/invoices/${id}/files`, fd);
      await loadFiles();
    } catch (e) {
      setError(e.response?.data?.message || 'Upload failed (max 25 MB per file)');
    } finally { setUploading(false); }
  };

  const openFile = async (f, download = false) => {
    try {
      const r = await api.get(`/invoices/files/${f.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: f.mime_type || 'application/octet-stream' }));
      if (download) {
        const a = document.createElement('a');
        a.href = url; a.download = f.original_name;
        document.body.appendChild(a); a.click(); a.remove();
      } else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { setError('Could not open that file'); }
  };

  const removeFile = async (f) => {
    if (!window.confirm(`Remove "${f.original_name}"?`)) return;
    try { await api.delete(`/invoices/files/${f.id}`); await loadFiles(); }
    catch { setError('Could not remove that file'); }
  };

  const prettySize = (b) => b == null ? '' :
    b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`;
  const fileIcon = (m) => /pdf/i.test(m || '') ? <PdfIcon fontSize="small" sx={{ color: 'error.main' }} />
    : /^image\//i.test(m || '') ? <ImageIcon fontSize="small" sx={{ color: 'info.main' }} />
    : <InsertDriveFile fontSize="small" sx={{ color: 'text.secondary' }} />;

  const openPdf = async (download = false) => {
    try {
      const r = await api.get(`/invoices/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      if (download) {
        const a = document.createElement('a');
        a.href = url; a.download = `Invoice_${inv?.invoice_number || id}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      } else {
        window.open(url, '_blank');
      }
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { alert('Failed to open PDF'); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  if (error)   return <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>;
  if (!inv)    return null;

  const totalQty = items.reduce((s, i) => s + parseFloat(i.quantity || 0), 0);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate('/invoices')}><ArrowBack /></IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5, gap: 1 }}>
            <Typography variant="h6" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
              {inv.invoice_number}
            </Typography>
            <StatusChip status={inv.status} />
          </Stack>
          <Typography variant="h6" sx={{ fontSize: { xs: '1.05rem', md: '1.25rem' }, fontWeight: 700 }}>
            {inv.beneficiary_name}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
          <Button variant="outlined" size="small" startIcon={<PictureAsPdf />} onClick={() => openPdf(false)}>View PDF</Button>
          <Button variant="outlined" size="small" startIcon={<Download />} onClick={() => openPdf(true)}>Download</Button>
          <Button variant="contained" size="small" startIcon={<Edit />} onClick={() => navigate(`/invoices/${id}/edit`)}>Edit</Button>
        </Stack>
      </Box>

      <Grid container spacing={2}>
        {/* Invoice meta */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Receipt fontSize="small" /> Invoice Details
            </Typography>
            <Field label="Invoice Number" value={inv.invoice_number} mono />
            <Field label="Invoice Date"   value={fmtDate(inv.invoice_date)} />
            {inv.ship_date && <Field label="Ship Date" value={fmtDate(inv.ship_date)} />}
            <Field label="Currency"       value={inv.currency} />
            {inv.exchange_rate && <Field label="Exchange Rate" value={inv.exchange_rate} />}
            <Field label="Payment Terms"  value={inv.payment_terms} />
            {inv.purpose_of_remittance && <Field label="Purpose" value={inv.purpose_of_remittance} />}
          </Paper>
        </Grid>

        {/* Beneficiary */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Business fontSize="small" /> Beneficiary
            </Typography>
            <Field label="Name"    value={inv.beneficiary_name} />
            <Field label="Address" value={inv.beneficiary_address} />
            {inv.contact_person && <Field label="Contact" value={inv.contact_person} />}
            {inv.contact_email   && <Field label="Email"   value={inv.contact_email} />}
          </Paper>
        </Grid>

        {/* Bank */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccountBalance fontSize="small" /> Bank Details
            </Typography>
            {inv.bank_name      && <Field label="Bank"           value={inv.bank_name} />}
            {inv.account_number && <Field label="Account #"      value={inv.account_number} mono />}
            {inv.iban           && <Field label="IBAN"           value={inv.iban} mono />}
            {inv.swift_code     && <Field label="SWIFT"          value={inv.swift_code} mono />}
            {inv.bank_address   && <Field label="Bank Address"   value={inv.bank_address} />}
          </Paper>
        </Grid>

        {/* Items */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Items</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell>HSN</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell>Unit</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>No items.</TableCell></TableRow>
                  ) : items.map((it, i) => (
                    <TableRow key={it.id}>
                      <TableCell sx={{ color: 'text.secondary', fontSize: 11 }}>{i + 1}</TableCell>
                      <TableCell><Typography variant="body2" fontWeight={600}>{it.description}</Typography></TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{it.sku || '—'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{it.hsn_code || '—'}</TableCell>
                      <TableCell align="right">{fmtQty(it.quantity)}</TableCell>
                      <TableCell>{it.unit || '—'}</TableCell>
                      <TableCell align="right">{fmtPrice(it.unit_price, inv.currency)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtMoney(it.amount, inv.currency)}</TableCell>
                    </TableRow>
                  ))}
                  {items.length > 0 && (
                    <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid', borderColor: 'divider', bgcolor: 'action.hover' } }}>
                      <TableCell colSpan={4}>TOTAL</TableCell>
                      <TableCell align="right">{fmtQty(totalQty)}</TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell align="right">{fmtMoney(inv.total_amount, inv.currency)}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Description / Notes */}
        {(inv.description || inv.notes) && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2.5 }}>
              {inv.description && <Field label="Description" value={inv.description} />}
              {inv.notes && <Field label="Notes" value={inv.notes} />}
            </Paper>
          </Grid>
        )}

        {/* Miscellaneous attachments */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachFile fontSize="small" /> Attachments{files.length ? ` (${files.length})` : ''}
              </Typography>
              <Button size="small" variant="contained" startIcon={<UploadFile />}
                disabled={uploading} onClick={() => fileRef.current?.click()}>
                {uploading ? 'Uploading…' : 'Add Files'}
              </Button>
            </Box>
            <input ref={fileRef} hidden type="file" multiple
              onChange={(e) => { uploadFiles(e.target.files); e.target.value = ''; }} />

            {files.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No files attached. Add packing lists, test reports, supplier chats, datasheets — anything
                that belongs with this proforma.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {files.map(f => (
                      <TableRow key={f.id} hover>
                        <TableCell sx={{ width: 34 }}>{fileIcon(f.mime_type)}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-all' }}>
                            {f.original_name}
                          </Typography>
                          {f.description && (
                            <Typography variant="caption" color="text.secondary">{f.description}</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                          {prettySize(f.size_bytes)}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                          {fmtDate(f.created_at)}{f.uploaded_by_name ? ` · ${f.uploaded_by_name}` : ''}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <IconButton size="small" title="Open" onClick={() => openFile(f, false)}
                            sx={{ color: 'primary.main' }}><OpenInNew fontSize="small" /></IconButton>
                          <IconButton size="small" title="Download" onClick={() => openFile(f, true)}
                            sx={{ color: 'text.secondary' }}><Download fontSize="small" /></IconButton>
                          <IconButton size="small" title="Remove" onClick={() => removeFile(f)}
                            sx={{ color: 'error.main' }}><Delete fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
